import Vue from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import App from './App.vue';
import Vue2Filters from 'vue2-filters';
import { BootstrapVue, ToastPlugin } from 'bootstrap-vue';
import PosthogPlugin from '@/plugins/posthog';
import router from './router';
import vuetify from '@/plugins/vuetify';
import store from '@/plugins/vuex';
import i18n from '@/plugins/i18n';
import * as config from './shared/config/config';
import * as bootstrapVueConfig from './shared/config/config-bootstrap-vue';
import JhiItemCountComponent from './shared/jhi-item-count.vue';
import JhiSortIndicatorComponent from './shared/sort/jhi-sort-indicator.vue';
import SButton from '@/components/stemdo-components/s-button/SButton.vue';
import SModalCentered from '@/components/stemdo-components/s-modal-centered/SModalCentered.vue';
import SCardStemdoer from '@/components/stemdo-components/s-card-stemdoer/SCardStemdoer.vue';
import MetricsService from './sections/admin/metrics/metrics.service';
import LogsService from './sections/admin/logs/logs.service';
import ActivateService from './sections/account/activate/activate.service';
import RegisterService from './sections/account/register/register.service';
import UserManagementService from './sections/admin/user-management/user-management.service';
import AccountService from './sections/account/service/account.service';
import AlertService from './shared/alert/alert.service';
import ClientService from './sections/admin/entities/client/client.service';
import StemdoerService from './sections/admin/entities/stemdoer/stemdoer.service';
import ProjectService from './sections/admin/entities/project/project.service';
import CompanyService from './sections/admin/entities/company/company.service';
import '../content/scss/global.scss';
import '../content/scss/vendor.scss';
import TranslationService from '@/locale/translation.service';
import ResourceService from './shared/ResourceService.service';
import CertificationService from './sections/admin/entities/certification/certification.service';
import TeamRequestService from './sections/admin/entities/team-request/team-request.service';
import SoftSkillService from './sections/admin/entities/soft-skill/soft-skill.service';
import ClientRateService from './sections/admin/entities/client-rate/client-rate.service';
import NoResultFiltersReportService from './sections/admin/entities/no-result-filters-report/no-result-filters-report.service';
import configAxios from './shared/config/config-axios';
import { Authority } from '@/shared/security/authority';
import accountPages from '@/router/accountPages';
import localStorageFacade, { StorageKey } from './shared/local-store-facade/LocalStoreFacade';
import TechnologyService from './sections/admin/entities/technology/technology.service';
import VerticalService from './sections/admin/entities/vertical/vertical.service';
import AuthToken from './shared/security/AuthToken';
import ClientDepartmentService from './sections/admin/entities/client-department/client-department.service';
import NotificationService from './sections/admin/entities/notification/notification.service';
import PdfService from './sections/admin/entities/pdf/pdf.service';
import CarbonFootprintService from './sections/admin/entities/carbon-footprint/carbonFootprint.service';
import DocxService from '@/sections/admin/entities/docx/docx.service';
import { refreshToken } from './shared/security/RefreshToken';

Vue.config.productionTip = false;
config.initVueApp(Vue);
config.initFortAwesome(Vue);
bootstrapVueConfig.initBootstrapVue(Vue);
configAxios();
Vue.use(Vue2Filters);
Vue.use(ToastPlugin);
Vue.use(BootstrapVue);
Vue.use(PosthogPlugin);
Vue.component('font-awesome-icon', FontAwesomeIcon);
Vue.component('jhi-item-count', JhiItemCountComponent);
Vue.component('jhi-sort-indicator', JhiSortIndicatorComponent);
Vue.component('font-awesome-icon', FontAwesomeIcon);
Vue.component('SButton', SButton);
Vue.component('SModalCentered', SModalCentered);
Vue.component('SCardStemdoer', SCardStemdoer);

router.beforeResolve(async (to, from, next) => {
  if (to.name === 'ServerError' || to.name === 'NotFoundError' || to.name === 'Forbidden') {
    next();
    return;
  }

  const isAuthenticated = store.getters.authenticated;
  const authToken = localStorageFacade.get(StorageKey.AUTH_TOKEN);
  if (!authToken) {
    // If the page not require authentication, continue
    const publicRoutes = ['Home', 'Privacy', 'Cookies', 'Legal', 'Contact'];
    if (accountPages.find(page => page.name === to.name) || publicRoutes.includes(to.name)) {
      next();
      return;
    }
    // Otherwise, store where the user wants to go and redirect to login page
    localStorageFacade.set(StorageKey.REQUESTED_URL, to.fullPath);
    next({ name: 'OtpLogin', query: { next: to.fullPath } });
    return;
  } else {
    if (to.name === 'OtpLogin') {
      store.commit('filterStore/clearToInitState');
      store.commit('pagesStore/clearToInitState');
      store.commit('cartStore/clearCart');
      next({ name: 'Home' });
      return;
    }
    if (to.meta.authorities?.length !== 0 && !AuthToken.hasAnyAuthority(to.meta.authorities)) {
      // If the user is authenticated but does not have the required authority, redirect directly to Home
      next({ name: 'Home' });
      return;
    }

    if (isAuthenticated) {
      await refreshToken();
      if (to.name === 'OtpLogin') {
        next({ name: 'Home' });
      } else {
        next();
      }
    } else if (!isAuthenticated) {
      try {
        const accountResponse = await accountService.retrieveAccount();
        const account = accountResponse.data;

        store.commit('authenticate', account);
        const isClient = account.authorities.find((authority: any) => authority === Authority.CLIENT);
        if (isClient) await accountService.retrieveClientDetails(account.email);

        if (!store.getters.currentLanguage || store.getters.currentLanguage === '') {
          if (store.getters.currentLanguage !== account.langKey) {
            await store.commit('currentLanguage', account.langKey);
          }
        }

        if (localStorageFacade.get(StorageKey.REQUESTED_URL)) {
          next({ path: localStorageFacade.get(StorageKey.REQUESTED_URL) });
          localStorageFacade.remove(StorageKey.REQUESTED_URL);
        } else if (to.name === 'OtpLogin') next({ name: 'Home' });
        else next();
      } catch (error) {
        const statusCode = error.response.status;
        if (statusCode === 401) {
          localStorageFacade.set(StorageKey.REQUESTED_URL, to.fullPath);
          next({ name: 'OtpLogin' });
          return;
        }
        localStorageFacade.set(StorageKey.REQUESTED_URL, to.fullPath);
        next({ name: 'ServerError' });
      }
    }
  }

  next();
});

const translationService = new TranslationService(store, i18n);
const clientService = new ClientService();
const companyService = new CompanyService();
const accountService = new AccountService(store, translationService, clientService);

const vue = new Vue({
  el: '#app',
  components: { App },
  template: '<App/>',
  router,
  provide: {
    notificationService: new NotificationService(),
    activateService: () => new ActivateService(),
    registerService: new RegisterService(),
    userManagementService: () => new UserManagementService(),
    logsService: () => new LogsService(),
    metricsService: () => new MetricsService(),
    clientService: () => clientService,
    stemdoerService: new StemdoerService(),
    teamRequestService: new TeamRequestService(),
    resourceService: new ResourceService(),
    certificationService: new CertificationService(),
    softSkillService: new SoftSkillService(),
    translationService: translationService,
    // jhipster-needle-add-entity-service-to-main - JHipster will import entities services here
    accountService: () => accountService,
    alertService: () => new AlertService(),
    clientRateService: new ClientRateService(),
    noResultFiltersReportService: new NoResultFiltersReportService(),
    technologyService: new TechnologyService(),
    verticalService: new VerticalService(),
    clientDepartmentService: () => new ClientDepartmentService(),
    pdfService: new PdfService(),
    docxService: new DocxService(),
    companyService: new CompanyService(),
    carbonFootprintService: new CarbonFootprintService(),
    projectService: new ProjectService()
  },
  i18n,
  store,
  vuetify
});
